/**
 * FattureInCloudPanel — piccolo pannello sidebar per gestire il collegamento
 * OAuth 2.0 a Fatture in Cloud.
 *
 * - Se collegato: mostra azienda e chi ha collegato. Solo Gianluca vede "Scollega".
 * - Se NON collegato: solo Gianluca vede il pulsante "Collega Fatture in Cloud".
 *   Gli altri admin vedono "Chiedi a Gianluca di collegarlo".
 *
 * Dopo il collegamento OAuth, FiC reindirizza al backend `/api/fatture-cloud/oauth/callback`
 * che risponde con una pagina HTML "Fatture in Cloud collegato ✓" e chiude la finestra.
 * Il pannello ricontrolla lo stato al focus della finestra.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Receipt, ExternalLink, RefreshCw, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function FattureInCloudPanel() {
  const [status, setStatus] = useState(null); // {connected, can_authorize, ...}
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/fatture-cloud/status");
      setStatus(r.data || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore stato Fatture in Cloud");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // Aggiorna quando la finestra riprende focus (l'utente torna dal popup OAuth)
    const onFocus = () => loadStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadStatus]);

  const handleConnect = async () => {
    try {
      const r = await api.get("/admin/fatture-cloud/authorize");
      const url = r.data?.authorize_url;
      if (!url) {
        toast.error("URL di autorizzazione non ricevuto");
        return;
      }
      // Apri in nuova tab. Al ritorno il backend serve la pagina "collegato" che
      // chiude la finestra; noi ricontrolliamo lo stato al focus (vedi useEffect).
      window.open(url, "fic_auth", "width=680,height=780,noopener");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore avvio autorizzazione");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Scollegare Fatture in Cloud? Gli altri admin non potranno più interrogarlo tramite AI.")) return;
    setDisconnecting(true);
    try {
      await api.post("/admin/fatture-cloud/disconnect");
      toast.success("Fatture in Cloud scollegato");
      await loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
        <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold flex items-center gap-1.5">
          <Receipt size={12} /> Fatture in Cloud
        </div>
        <div className="mt-2 text-xs text-slate-400 flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> Verifica collegamento…
        </div>
      </div>
    );
  }

  const connected = !!status?.connected;
  const canAuthorize = !!status?.can_authorize;

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 shadow-sm"
      data-testid="fatture-cloud-panel"
    >
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-md ${connected ? "bg-emerald-50" : "bg-slate-100"}`}>
            <Receipt size={14} className={connected ? "text-emerald-700" : "text-slate-500"} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold flex items-center gap-1.5">
              Contabilità
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-300"}`}
                title={connected ? "Collegato" : "Non collegato"}
              />
            </div>
            <h2 className="font-display text-base text-ink leading-tight">Fatture in Cloud</h2>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {connected ? (
          <>
            <div className="flex items-start gap-2 text-xs text-slate-700">
              <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-ink">
                  {status.company_name || `Azienda #${status.company_id || "?"}`}
                </div>
                {status.authorized_by_email && (
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Collegato da <span className="font-mono">{status.authorized_by_email}</span>
                  </div>
                )}
                <div className="text-[10px] text-slate-500 mt-1 leading-snug">
                  L&apos;assistente AI può ora rispondere su fatture, incassi, clienti, fornitori e fatturato.
                </div>
              </div>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={loadStatus}
                className="flex-1 text-[10px] tracking-wider uppercase font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1.5 rounded flex items-center justify-center gap-1 transition"
                data-testid="fic-refresh"
                title="Ricontrolla stato"
              >
                <RefreshCw size={11} /> Aggiorna
              </button>
              {canAuthorize && (
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="text-[10px] tracking-wider uppercase font-semibold bg-white hover:bg-rose-50 text-slate-600 hover:text-rose-700 border border-slate-200 hover:border-rose-200 px-2 py-1.5 rounded flex items-center gap-1 transition disabled:opacity-50"
                  data-testid="fic-disconnect"
                >
                  {disconnecting ? <Loader2 size={11} className="animate-spin" /> : null}
                  Scollega
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-2 text-xs text-slate-600">
              <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                {canAuthorize ? (
                  <div>
                    <div className="text-slate-700 font-medium">Non ancora collegato</div>
                    <div className="text-[10px] text-slate-500 mt-1 leading-snug">
                      Collegalo una sola volta per abilitare le domande dell&apos;AI su fatture, incassi e clienti.
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-slate-700 font-medium">Non collegato</div>
                    <div className="text-[10px] text-slate-500 mt-1 leading-snug">
                      Chiedi a <span className="font-mono">gianluca@italian-mood.it</span> di collegare il conto Fatture in Cloud.
                    </div>
                  </div>
                )}
              </div>
            </div>
            {canAuthorize && (
              <button
                type="button"
                onClick={handleConnect}
                className="w-full bg-ink hover:bg-slate-800 text-white text-[10px] tracking-wider uppercase font-semibold px-2.5 py-2 rounded flex items-center justify-center gap-1.5 transition"
                data-testid="fic-connect"
              >
                <ExternalLink size={12} /> Collega Fatture in Cloud
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
